import { useMutation, useSuspenseQuery } from "@apollo/client";
import { gql } from "generated-graphql";

/**
 * Deliberately separate from `GetWasteStream`: the profile is customer-owned
 * data with no date/view/tag dimension, while the drawer's other query is
 * re-issued whenever the facility filters change. Keeping them apart means a
 * filter change does not refetch the profile, and a profile save does not
 * refetch every manifest aggregate on the drawer.
 *
 * It reads `wasteStreamProfileView`, not `wasteStream(id:)`. That resolver runs
 * the WasteLineUnified join for its shipment aggregates whichever fields are
 * selected, so going through it made opening the drawer run that join twice,
 * the second time with no date bounds to prune partitions with.
 */
export const GET_WASTE_STREAM_PROFILE = gql(`
  query GetWasteStreamProfile($id: ID!) {
    wasteStream: wasteStreamProfileView(wasteStreamId: $id) {
      id
      name
      facilityId
      dotProperShippingName
      dotIdNumber
      dotHazardClass
      dotPackingGroup
      dotHazardous
      profile {
        id
        physicalState
        phLow
        phHigh
        flashPointF
        specificGravity
        physicalDescription
        isIgnitable
        isCorrosive
        isReactive
        isToxic
        generatingProcess
        basis
        profileNumber
        tsdfName
        tsdfProfileId
        profileDocument {
          id
          title
          fileExtension
          downloadLink
          updatedAt
          sizeMegabytes
          # Only an uploaded sheet can be read by the extractor — a generated
          # summary is our own render of these columns — so the auto-fill entry
          # needs the live type to know whether to offer itself.
          documentType
        }
        recertifyByDate
        recertifyByDateOverride
        testFrequency
        testFrequencyEvery
        testStatus
        extractionApplied
        completeness {
          complete
          missing
        }
        constituents {
          id
          name
          casNumber
          minPercent
          maxPercent
        }
        tests {
          id
          testDate
          type
          method
          labName
          resultSummary
          resultDocument {
            id
            title
            downloadLink
          }
        }
      }
    }
  }
`);

export const useWasteStreamProfile = (wasteStreamId: string) =>
  useSuspenseQuery(GET_WASTE_STREAM_PROFILE, { variables: { id: wasteStreamId } });

export const WASTE_PROFILE_INPUT_VALIDATOR = gql(`
  query WasteProfileInputValidator($input: WasteProfileInput!) {
    wasteProfileInputValidator(input: $input) {
      key
      level
      message
      modelName
    }
  }
`);

const UPSERT_WASTE_PROFILE = gql(`
  mutation UpsertWasteProfile($input: WasteProfileInput!) {
    upsertWasteProfile(input: $input) {
      id
    }
  }
`);

const SET_WASTE_STREAM_DOT_PROFILE = gql(`
  mutation SetWasteStreamDotProfile($input: WasteStreamDotProfileInput!) {
    setWasteStreamDotProfile(input: $input)
  }
`);

const ATTACH_WASTE_PROFILE_DOCUMENT = gql(`
  mutation AttachWasteProfileDocument($wasteStreamId: ID!, $documentId: ID!) {
    attachWasteProfileDocument(wasteStreamId: $wasteStreamId, documentId: $documentId)
  }
`);

const GENERATE_WASTE_PROFILE_SUMMARY = gql(`
  mutation GenerateWasteProfileSummary($wasteStreamId: ID!) {
    generateWasteProfileSummary(wasteStreamId: $wasteStreamId) {
      id
      title
      downloadLink
      updatedAt
    }
  }
`);

/**
 * The AI reading of a profile document, for the review grid.
 *
 * Polled while the extraction is in flight rather than pushed, matching how the
 * rest of Document AI reports progress. `network-only` on the poll so a cached
 * PENDING is not what the reviewer keeps being shown.
 */
export const GET_EXTRACTED_WASTE_PROFILE = gql(`
  query GetExtractedWasteProfile($documentId: ID!) {
    extractedWasteProfile(documentId: $documentId) {
      documentId
      extractionStatus
      extractionError
      readAt
      detectedBasis
      values {
        ...ExtractedProfileValuesFields
      }
      asExtracted {
        ...ExtractedProfileValuesFields
      }
      confidence {
        fieldPath
        confidence
      }
    }
  }
`);

/**
 * Selected twice — once for what the reviewer sees, once for what the model
 * read — so the grid can show the extraction beside a value someone has changed.
 */
gql(`
  fragment ExtractedProfileValuesFields on ExtractedProfileValues {
    physicalState
    phLow
    phHigh
    flashPointF
    specificGravity
    physicalDescription
    isIgnitable
    isCorrosive
    isReactive
    isToxic
    generatingProcess
    profileNumber
    tsdfName
    tsdfProfileId
    dotProperShippingName
    dotIdNumber
    dotHazardClass
    dotPackingGroup
    dotHazardous
    constituents {
      name
      casNumber
      minPercent
      maxPercent
    }
  }
`);

const EXTRACT_WASTE_PROFILE_DOCUMENT = gql(`
  mutation ExtractWasteProfileDocument($documentId: ID!) {
    extractWasteProfileDocument(documentId: $documentId)
  }
`);

const APPLY_EXTRACTED_PROFILE = gql(`
  mutation ApplyExtractedProfile($input: ApplyExtractedProfileInput!) {
    applyExtractedProfile(input: $input) {
      id
    }
  }
`);

const ADD_WASTE_PROFILE_TEST = gql(`
  mutation AddWasteProfileTest($input: AddWasteProfileTestInput!) {
    addWasteProfileTest(input: $input) {
      id
    }
  }
`);

const EDIT_WASTE_PROFILE_TEST = gql(`
  mutation EditWasteProfileTest($input: EditWasteProfileTestInput!) {
    editWasteProfileTest(input: $input) {
      id
    }
  }
`);

const DELETE_WASTE_PROFILE_TEST = gql(`
  mutation DeleteWasteProfileTest($id: ID!) {
    deleteWasteProfileTest(id: $id)
  }
`);

const SET_PROFILE_TEST_SCHEDULE = gql(`
  mutation SetProfileTestSchedule(
    $wasteStreamId: ID!
    $frequency: Frequency
    $every: Int
    $recertifyByDateOverride: Date
  ) {
    setProfileTestSchedule(
      wasteStreamId: $wasteStreamId
      frequency: $frequency
      every: $every
      recertifyByDateOverride: $recertifyByDateOverride
    )
  }
`);

/**
 * Every profile write refetches the one profile query. The mutations return
 * only ids (or a single Document), so there is nothing to merge into the
 * cache by hand, and `recertifyByDate` / `testStatus` / `completeness` are all
 * server-derived from the write — a hand-rolled cache update would have to
 * reimplement that derivation to stay honest.
 */
const refetchProfile = { refetchQueries: [GET_WASTE_STREAM_PROFILE] };

export const useUpsertWasteProfile = () => useMutation(UPSERT_WASTE_PROFILE, refetchProfile);
export const useSetWasteStreamDotProfile = () =>
  useMutation(SET_WASTE_STREAM_DOT_PROFILE, refetchProfile);
export const useAttachWasteProfileDocument = () =>
  useMutation(ATTACH_WASTE_PROFILE_DOCUMENT, refetchProfile);
export const useGenerateWasteProfileSummary = () =>
  useMutation(GENERATE_WASTE_PROFILE_SUMMARY, refetchProfile);
export const useAddWasteProfileTest = () => useMutation(ADD_WASTE_PROFILE_TEST, refetchProfile);
export const useEditWasteProfileTest = () => useMutation(EDIT_WASTE_PROFILE_TEST, refetchProfile);
export const useDeleteWasteProfileTest = () =>
  useMutation(DELETE_WASTE_PROFILE_TEST, refetchProfile);
export const useSetProfileTestSchedule = () =>
  useMutation(SET_PROFILE_TEST_SCHEDULE, refetchProfile);

export const useExtractWasteProfileDocument = () => useMutation(EXTRACT_WASTE_PROFILE_DOCUMENT);

/**
 * Refetches the profile like every other profile write, and the reading beside
 * it: an applied candidate changes status, so leaving the reading cached would
 * let a second open of the dialog offer to apply what was just applied.
 */
export const useApplyExtractedProfile = () =>
  useMutation(APPLY_EXTRACTED_PROFILE, {
    refetchQueries: [GET_WASTE_STREAM_PROFILE, GET_EXTRACTED_WASTE_PROFILE],
  });
